import { useState, useEffect } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button from "components/button";
import DataTable from "components/data-table";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { statusTone } from "lib/statusTone";
import { formatDate } from "utils/format";
import roleLabel from "./components/roleLabel";

const isPastDate = (value) => new Date(value) < new Date();

const invitationStatus = (invitation) => {
  if (invitation.status === "accepted") return { label: "Accepted", tone: "success" };
  if (invitation.status === "revoked") return { label: "Revoked", tone: "danger" };
  if (invitation.status === "expired" || isPastDate(invitation.expiresAt)) {
    return { label: "Expired", tone: "danger" };
  }
  return { label: "Pending", tone: statusTone("pending") };
};

const PendingInvitations = ({ refresh }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const confirm = useConfirm();

  const fetchInvitations = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/v1/invitations");
      if (response.data.success) {
        // Accepted invitations are users now (listed under Current Staff);
        // revoked and expired ones are hidden.
        const activeInvitations = (response.data.invitations || []).filter(
          (inv) => inv.status !== "revoked" && inv.status !== "expired" && inv.status !== "accepted"
        );
        setInvitations(activeInvitations);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [refresh]);

  const handleResend = async (invitation) => {
    setResendingId(invitation.id);
    try {
      const response = await AtriumApi.post(`/v1/invitations/${invitation.id}/resend`);
      if (response.data.success) {
        notify.success("Invitation resent");
        fetchInvitations();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not resend invitation"));
    } finally {
      setResendingId(null);
    }
  };

  const handleRevoke = async (invitation) => {
    const confirmed = await confirm({
      title: `Revoke invitation for "${invitation.email}"?`,
      message: "The invitation link will stop working. You can send a new invitation later.",
      confirmLabel: "Revoke",
      tone: "danger",
    });
    if (!confirmed) return;

    setRevokingId(invitation.id);
    try {
      const response = await AtriumApi.delete(`/v1/invitations/${invitation.id}`);
      if (response.data.success) {
        notify.success("Invitation revoked");
        fetchInvitations();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not revoke invitation"));
    } finally {
      setRevokingId(null);
    }
  };

  if (loadError) {
    return (
      <ErrorState
        message={getApiErrorMessage(loadError, "Could not load invitations.")}
        onRetry={fetchInvitations}
      />
    );
  }

  const columns = [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ getValue }) => <span className="font-medium text-fg">{getValue()}</span>,
    },
    { accessorKey: "role", header: "Role", cell: ({ getValue }) => roleLabel(getValue()) },
    {
      accessorKey: "sentAt",
      header: "Sent",
      meta: { className: "whitespace-nowrap" },
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      meta: { className: "whitespace-nowrap" },
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const { label, tone } = invitationStatus(row.original);
        return <Badge tone={tone}>{label}</Badge>;
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "right" },
      cell: ({ row }) => {
        const invitation = row.original;
        const canRevoke = invitation.status === "pending";
        const canResend = canRevoke && !isPastDate(invitation.expiresAt);
        return (
          <div className="flex items-center justify-end gap-2">
            {canResend && (
              <Button
                variant="secondary"
                size="sm"
                loading={resendingId === invitation.id}
                onClick={() => handleResend(invitation)}
              >
                Resend
              </Button>
            )}
            {canRevoke && (
              <Button
                variant="ghost"
                size="sm"
                loading={revokingId === invitation.id}
                onClick={() => handleRevoke(invitation)}
              >
                Revoke
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      variant="plain"
      aria-label="Pending invitations"
      columns={columns}
      data={invitations}
      loading={loading}
      getRowId={(inv) => String(inv.id)}
      emptyState={<EmptyState variant="inline" title="No pending invitations." />}
    />
  );
};

export default PendingInvitations;
